"use client";

import { useSearchParams } from "next/navigation";
import { useMemo } from "react";

/**
 * Safe wrapper for useSearchParams that extracts values immediately
 * to prevent React DevTools from serializing the searchParams object
 */
export function useSearchParamsSafe() {
  const searchParams = useSearchParams();
  // Extract all values immediately into a plain object
  return useMemo(() => {
    const result: Record<string, string | null> = {};
    // Extract each search param value immediately
    searchParams.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }, [searchParams]);
}

/**
 * Helper to extract a single search param value safely
 */
export function useSearchParamValue(key: string): string | null {
  const searchParams = useSearchParams();
  return useMemo(() => {
    return searchParams.get(key) || null;
  }, [searchParams, key]);
}

/**
 * Helper to extract multiple search param values safely
 */
export function useSearchParamValues(keys: string[]): Record<string, string | null> {
  const searchParams = useSearchParams();
  return useMemo(() => {
    const result: Record<string, string | null> = {};
    keys.forEach((key) => {
      result[key] = searchParams.get(key) || null;
    });
    return result;
  }, [searchParams, keys.join(",")]);
}
